import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import useChat from "../hooks/useChat";
import ChatMessage from "./ChatMessage";
import { getServiceNames } from "../data/quoteFlow";
import {
  quickActions,
  contactInfo,
  whatsappButtonText,
  emailTranscriptButtonText,
} from "../data/chatKnowledge";

function TypingIndicator() {
  return (
    <div className="flex gap-2.5 justify-start">
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyber-blue to-tech-green flex items-center justify-center shrink-0 mt-0.5">
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-black">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1.5">
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  );
}

const btnBase =
  "px-3 py-1.5 text-[11px] font-medium rounded-full border border-white/10 bg-white/5 text-gray-300 hover:text-white hover:bg-white/10 hover:border-cyber-blue/30 transition-all";

export default function ChatWidget() {
  const {
    messages,
    isOpen,
    isTyping,
    leadInfo,
    isCollectingLead,
    emailSending,
    quoteFlow,
    toggleChat,
    closeChat,
    sendMessage,
    clearChat,
    sendEmailTranscript,
    openWhatsApp,
    selectQuoteService,
    selectQuoteOption,
    cancelQuote,
    handleQuoteEmailPrompt,
    handleQuoteEmailNoThanks,
    openQuoteWhatsApp,
  } = useChat();

  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const handleSend = () => {
    if (!input.trim()) return;
    sendMessage(input.trim());
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickAction = (msg) => {
    sendMessage(msg);
  };

  const isQuoteActive = quoteFlow.active;
  const isQuoteSelectingService = isQuoteActive && !quoteFlow.service;
  const isQuoteAnswering = isQuoteActive && quoteFlow.service && !quoteFlow.result;
  const isQuoteCompleted = isQuoteActive && quoteFlow.result && !quoteFlow.emailSent;

  const showQuickActions = !isQuoteActive && messages.length <= 1 && !isTyping && !isCollectingLead;
  const showLeadActions = leadInfo && !isCollectingLead && !isQuoteActive;

  const currentStep =
    isQuoteAnswering && quoteFlow.service
      ? quoteFlow.service.steps[quoteFlow.stepIndex]
      : null;

  const quoteProgressBar =
    isQuoteAnswering && quoteFlow.service
      ? `${quoteFlow.stepIndex + 1}/${quoteFlow.service.steps.length}`
      : null;

  return (
    <div className="fixed bottom-5 right-5 z-50 font-inter" id="carium-chat-widget">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="chat-window"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="mb-4 w-[calc(100vw-2.5rem)] sm:w-[380px] h-[520px] max-h-[75vh] rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40 flex flex-col bg-[#0d0d0d]"
          >
            {/* Header */}
            <div className="relative px-5 py-4 bg-gradient-to-r from-cyber-blue/20 to-tech-green/20 border-b border-white/10 shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-cyber-blue/5 to-tech-green/5" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyber-blue to-tech-green flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-black">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm leading-tight">
                      Carium Tech
                    </h3>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-tech-green animate-pulse" />
                      <span className="text-gray-400 text-[11px]">
                        {isQuoteActive
                          ? `Quote ${quoteProgressBar || ""}`
                          : leadInfo
                          ? `Hi, ${leadInfo.name}`
                          : "Online"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isQuoteActive && (
                    <button
                      onClick={cancelQuote}
                      className="text-gray-400 hover:text-red-400 transition-colors p-1 rounded-lg hover:bg-white/10"
                      aria-label="Cancel quote"
                      title="Cancel quote"
                    >
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                      </svg>
                    </button>
                  )}
                  {messages.length > 0 && (
                    <button
                      onClick={clearChat}
                      className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
                      aria-label="Clear chat"
                      title="Clear chat"
                    >
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={closeChat}
                    className="text-gray-400 hover:text-white transition-colors p-1"
                    aria-label="Close chat"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
              {messages.map((msg) => (
                <ChatMessage key={msg.id} message={msg} />
              ))}
              {isTyping && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>

            {/* Quote Progress Bar */}
            {isQuoteAnswering && quoteProgressBar && (
              <div className="px-4 pb-1 shrink-0">
                <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyber-blue to-tech-green rounded-full transition-all duration-500"
                    style={{
                      width: `${((quoteFlow.stepIndex + 1) / quoteFlow.service.steps.length) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Quote: Service Selection */}
            {isQuoteSelectingService && (
              <div className="px-4 pb-2 flex flex-wrap gap-2 shrink-0">
                {getServiceNames().map((name) => (
                  <button key={name} onClick={() => selectQuoteService(name)} className={btnBase}>
                    {name}
                  </button>
                ))}
              </div>
            )}

            {/* Quote: Step Options */}
            {currentStep && (
              <div className="px-4 pb-2 flex flex-wrap gap-2 shrink-0">
                {currentStep.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => selectQuoteOption(opt.value)}
                    className={btnBase}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* Quote: Post-completion actions */}
            {isQuoteCompleted && (
              <div className="px-4 pb-2 flex flex-wrap gap-2 shrink-0">
                <button
                  onClick={handleQuoteEmailPrompt}
                  className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium rounded-full bg-cyber-blue/20 text-cyber-blue border border-cyber-blue/30 hover:bg-cyber-blue/30 transition-all"
                >
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                  Email Quote
                </button>
                <button
                  onClick={openQuoteWhatsApp}
                  className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium rounded-full bg-[#25D366]/20 text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/30 transition-all"
                >
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  {whatsappButtonText}
                </button>
                <button
                  onClick={handleQuoteEmailNoThanks}
                  className={btnBase}
                >
                  No Thanks
                </button>
              </div>
            )}

            {/* Lead Action Buttons */}
            {showLeadActions && (
              <div className="px-4 pb-2 flex flex-wrap gap-2 shrink-0">
                <button
                  onClick={sendEmailTranscript}
                  disabled={emailSending}
                  className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium rounded-full bg-cyber-blue/20 text-cyber-blue border border-cyber-blue/30 hover:bg-cyber-blue/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                  {emailSending ? "Sending..." : emailTranscriptButtonText}
                </button>
                <button
                  onClick={openWhatsApp}
                  className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium rounded-full bg-[#25D366]/20 text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/30 transition-all"
                >
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  {whatsappButtonText}
                </button>
              </div>
            )}

            {/* Quick Actions */}
            {showQuickActions && (
              <div className="px-4 pb-2 flex flex-wrap gap-2 shrink-0">
                {quickActions.map((action) => (
                  <button
                    key={action.label}
                    onClick={() => handleQuickAction(action.message)}
                    className={btnBase}
                  >
                    {action.label}
                  </button>
                ))}
                <Link
                  to="/contact"
                  onClick={closeChat}
                  className="px-3 py-1.5 text-[11px] font-medium rounded-full bg-tech-green/20 text-tech-green border border-tech-green/30 hover:bg-tech-green/30 transition-all"
                >
                  Contact Us
                </Link>
              </div>
            )}

            {/* Input */}
            <div className="px-4 py-3 border-t border-white/10 shrink-0">
              <div className="flex items-center gap-2 bg-white/5 rounded-xl border border-white/10 focus-within:border-cyber-blue/40 transition-colors">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    quoteFlow.collectingEmail
                      ? "your@email.com"
                      : isCollectingLead
                      ? "Your name, email@example.com"
                      : isQuoteSelectingService
                      ? "Type a service name..."
                      : currentStep
                      ? "Select an option above or type..."
                      : "Type a message..."
                  }
                  className="flex-1 bg-transparent text-white text-sm px-4 py-3 placeholder-gray-600 focus:outline-none"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim()}
                  className="p-2.5 mr-1 rounded-lg bg-cyber-blue text-black hover:bg-cyber-blue/90 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  aria-label="Send message"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              </div>
              <p className="text-center text-[10px] text-gray-600 mt-2">
                Powered by Carium Tech &middot;{" "}
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-cyber-blue/60 hover:text-cyber-blue transition-colors"
                >
                  {contactInfo.email}
                </a>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button
        onClick={toggleChat}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        className={`ml-auto flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all duration-300 ${
          isOpen
            ? "bg-white/10 border border-white/20 shadow-none"
            : "bg-cyber-blue hover:shadow-[0_0_25px_rgba(0,163,255,0.4)]"
        }`}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.svg
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
              viewBox="0 0 24 24"
              className="w-6 h-6 fill-white"
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </motion.svg>
          ) : (
            <motion.svg
              key="chat"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ duration: 0.15 }}
              viewBox="0 0 24 24"
              className="w-6 h-6 fill-black"
            >
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
            </motion.svg>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Pulse ring when closed */}
      {!isOpen && (
        <span className="absolute bottom-0 right-0 w-14 h-14 rounded-full bg-cyber-blue/30 animate-ping pointer-events-none" />
      )}
    </div>
  );
}
